/**
 * auth/AuthProvider — session context for the SPA. Exposes the current auth state and
 * `login` / `logout`. `login` verifies a candidate token by calling an authed endpoint
 * before persisting it, so a bad token never appears "logged in". A 401 anywhere (via
 * the session store) flips the whole app back to logged-out.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api, UnauthorizedError } from '../api/client';
import { clearToken, getToken, setToken, subscribe } from './session';

interface AuthContextValue {
  readonly authed: boolean;
  readonly login: (token: string) => Promise<void>;
  readonly logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [authed, setAuthed] = useState<boolean>(() => getToken() !== null);

  useEffect(() => subscribe((token) => setAuthed(token !== null)), []);

  const login = useCallback(async (token: string): Promise<void> => {
    const candidate = token.trim();
    if (!candidate) throw new Error('Enter your dashboard token.');
    // Persist first so the client attaches the bearer, then probe an authed route.
    setToken(candidate);
    try {
      await api.state();
    } catch (err) {
      clearToken();
      if (err instanceof UnauthorizedError) throw new Error('Invalid token.');
      throw err;
    }
  }, []);

  const logout = useCallback(() => clearToken(), []);

  const value = useMemo<AuthContextValue>(() => ({ authed, login, logout }), [authed, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
